import {
  hasValue,
  isEmpty,
  isFunction,
  localStorage as localStorageHelper,
} from 'swipeclock-frontend';
import { isNativeOfflineShell } from 'helpers/core/url';
import { OFFLINE_PUNCH_LABELS } from '../constants/offlinePunchLabels';
import {
  pickAllowedCacheFields,
  pickAllowedOfflinePunchFields,
} from './offlineStorageAllowList';
import {
  readOfflineWebClockItem,
  removeOfflineWebClockItem,
  writeOfflineWebClockItem,
} from './offlineWebClockStorage';
import { readOfflinePunchQueue } from './offlinePunchStorage';
import { getClockConnectCacheStorageKey } from './webClockUserStorageKey';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const CLOCK_CONNECT_CACHE_TTL_MS = 7 * MS_PER_DAY;
export const MAX_CONFIRMED_PUNCHES = 100;
const CLOCK_CONNECT_CACHE_SOURCE = 'clockConnect';
let clockConnectCacheRevision = 0;
let hydrationSettledUserKey = null;
let cacheOpTail = null;
const cacheRevisionListeners = new Set();
const LEGACY_REQUIRED_FIELDS = [
  'clockPrompts',
  'displayButtons',
  'disabledButtonMap',
  'employeeName',
  'responseType',
  'intelligentClock',
  'geoLocationRequired',
];

const PUNCH_TO_BUTTON_VALUE = {
  PunchIn: 'IN',
  PunchOut: 'OUT',
  StartBreak: 'OUTBREAK',
  EndBreak: 'INBREAK',
  StartMeal: 'OUTMEAL',
  EndMeal: 'INMEAL',
  Transfer: 'TRANSFER',
};

const TOGGLE_PAIRS = {
  IN: 'OUT',
  OUT: 'IN',
  OUTBREAK: 'INBREAK',
  INBREAK: 'OUTBREAK',
  OUTMEAL: 'INMEAL',
  INMEAL: 'OUTMEAL',
};

const ALL_PUNCH_BUTTON_VALUES = [
  'IN',
  'OUT',
  'OUTBREAK',
  'INBREAK',
  'OUTMEAL',
  'INMEAL',
  'TRANSFER',
  'ESS',
];

const PUNCH_BUTTON_TRANSITIONS = {
  PunchIn: {
    enable: ['OUT', 'OUTBREAK', 'OUTMEAL', 'TRANSFER'],
    disable: ['IN'],
  },
  PunchOut: {
    enable: ['IN'],
    disable: ['OUT', 'OUTBREAK', 'OUTMEAL', 'TRANSFER'],
  },
  StartBreak: {
    exclusiveEnable: 'INBREAK',
  },
  EndBreak: {
    enable: ['OUT', 'OUTBREAK', 'OUTMEAL', 'TRANSFER'],
    disable: ['IN', 'INBREAK', 'INMEAL'],
  },
  StartMeal: {
    exclusiveEnable: 'INMEAL',
  },
  EndMeal: {
    enable: ['OUT', 'OUTBREAK', 'OUTMEAL', 'TRANSFER'],
    disable: ['IN', 'INBREAK', 'INMEAL'],
  },
  Transfer: {
    enable: ['TRANSFER'],
    disable: [],
  },
};

const getLastPunchDisplayLabel = (punch, fallback = '') =>
  OFFLINE_PUNCH_LABELS[punch] || fallback;

const notifyCacheRevisionChanged = () => {
  clockConnectCacheRevision += 1;
  cacheRevisionListeners.forEach((listener) => {
    try {
      listener(clockConnectCacheRevision);
    } catch (error) {
      console.warn(
        '[offlineClockConnectCache] Cache listener failed; continuing.',
        error
      );
    }
  });
};

export const getOfflineClockConnectCacheRevision = () =>
  clockConnectCacheRevision;

/**
 * Native secure storage is read asynchronously after load, so the cache is
 * not authoritative until hydration for this userKey has settled.
 * Always true outside the native offline shell.
 */
export const isOfflineClockCacheHydrationSettled = (userKey) => {
  if (!isNativeOfflineShell() || !hasValue(userKey)) {
    return true;
  }

  return hydrationSettledUserKey === userKey;
};

export const markOfflineClockCacheHydrationSettled = (userKey) => {
  if (!isNativeOfflineShell() || !hasValue(userKey)) {
    return;
  }

  hydrationSettledUserKey = userKey;
  notifyCacheRevisionChanged();
};

/** @internal Resets hydration-settled state between unit tests. */
export const resetOfflineClockCacheHydrationForTests = () => {
  hydrationSettledUserKey = null;
  cacheOpTail = null;
};

export const subscribeOfflineClockConnectCache = (listener) => {
  if (typeof listener !== 'function') {
    return () => {};
  }

  cacheRevisionListeners.add(listener);
  return () => {
    cacheRevisionListeners.delete(listener);
  };
};

const normalizeClockConnectPayload = (clockConnectData) =>
  pickAllowedCacheFields(clockConnectData);

const isLegacyCacheSnapshot = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') {
    return false;
  }

  return LEGACY_REQUIRED_FIELDS.every((field) => field in snapshot);
};

/**
 * Structural snapshot check only — does not clone or re-normalise prompts.
 */
const isValidCacheSnapshot = (snapshot) => {
  if (!snapshot || typeof snapshot !== 'object') {
    return false;
  }

  if (!hasValue(snapshot.cachedAt)) {
    return false;
  }

  if (
    'offlinePunch' in snapshot &&
    snapshot.offlinePunch !== null &&
    typeof snapshot.offlinePunch !== 'object'
  ) {
    return false;
  }

  if (hasValue(snapshot.payload)) {
    return typeof snapshot.payload === 'object';
  }

  return isLegacyCacheSnapshot(snapshot);
};

const getStorageKey = (userKey) => getClockConnectCacheStorageKey(userKey);

const isStorageAvailable = () =>
  localStorageHelper.isLocalStorageAvailable() || typeof window !== 'undefined';

/**
 * Build a snapshot from an already-sanitised payload (no re-normalisation).
 */
const buildCacheSnapshotFromSanitized = (
  sanitizedPayload,
  sanitizedOfflinePunch = null,
  confirmedPunches = null
) => {
  if (!sanitizedPayload || typeof sanitizedPayload !== 'object') {
    return null;
  }

  return {
    cachedAt: new Date().toISOString(),
    source: CLOCK_CONNECT_CACHE_SOURCE,
    payload: sanitizedPayload,
    ...(sanitizedOfflinePunch && { offlinePunch: sanitizedOfflinePunch }),
    ...(Array.isArray(confirmedPunches) &&
      confirmedPunches.length > 0 && { confirmedPunches }),
  };
};

const parseCacheSnapshot = (userKey) => {
  const storageKey = getStorageKey(userKey);
  if (!storageKey || !isStorageAvailable()) {
    return null;
  }

  try {
    const raw = readOfflineWebClockItem(storageKey);

    if (!hasValue(raw)) {
      return null;
    }

    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!isValidCacheSnapshot(parsed)) {
      console.warn(
        '[offlineClockConnectCache] Invalid cache format; ignoring.'
      );
      return null;
    }

    const cachedAtTime = new Date(parsed.cachedAt).getTime();

    if (
      Number.isNaN(cachedAtTime) ||
      Date.now() - cachedAtTime > CLOCK_CONNECT_CACHE_TTL_MS
    ) {
      removeOfflineWebClockItem(storageKey);
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn(
      '[offlineClockConnectCache] Corrupt cache data; ignoring.',
      error
    );
    removeOfflineWebClockItem(storageKey);
    return null;
  }
};

/**
 * Sole normaliser on the read path.
 */
const extractPayload = (snapshot) => {
  if (!snapshot) {
    return null;
  }

  if (hasValue(snapshot.payload)) {
    return normalizeClockConnectPayload(snapshot.payload);
  }

  if (isLegacyCacheSnapshot(snapshot)) {
    return normalizeClockConnectPayload(snapshot);
  }

  return null;
};

const getDateTimeMs = (value) => {
  if (!hasValue(value)) {
    return null;
  }

  const dateTimeMs = new Date(value).getTime();
  return Number.isNaN(dateTimeMs) ? null : dateTimeMs;
};

const getCacheTimeMs = (cachedAt) => {
  const cachedAtMs = getDateTimeMs(cachedAt);
  return hasValue(cachedAtMs) ? cachedAtMs : 0;
};

const sanitizeConfirmedPunches = (punches) => {
  if (!Array.isArray(punches)) {
    return [];
  }

  const seen = new Set();
  const recentPunches = punches.slice(-MAX_CONFIRMED_PUNCHES);

  return recentPunches.reduce((confirmedPunches, item) => {
    const punchMs = getDateTimeMs(item?.punchDateTime);

    if (!hasValue(item?.punch) || !hasValue(punchMs)) {
      return confirmedPunches;
    }

    const key = `${item.punch}|${item.punchDateTime}`;

    if (seen.has(key)) {
      return confirmedPunches;
    }

    seen.add(key);
    confirmedPunches.push({
      punch: item.punch,
      punchDateTime: item.punchDateTime,
    });
    return confirmedPunches;
  }, []);
};

/**
 * Expects punches already passed through sanitizeConfirmedPunches.
 */
const trimConfirmedPunches = (punches, payload) => {
  if (!Array.isArray(punches)) {
    return [];
  }

  const apiLastPunchMs = getDateTimeMs(payload?.lastPunchUTC);

  return punches
    .map((item) => ({ item, punchMs: getDateTimeMs(item.punchDateTime) }))
    .filter(
      ({ punchMs }) => !hasValue(apiLastPunchMs) || punchMs > apiLastPunchMs
    )
    .sort((left, right) => left.punchMs - right.punchMs)
    .slice(-MAX_CONFIRMED_PUNCHES)
    .map(({ item }) => item);
};

/**
 * Expects confirmedPunches already passed through sanitizeConfirmedPunches.
 */
const predictionSources = (confirmedPunches, queueItems) => {
  const confirmed = Array.isArray(confirmedPunches) ? confirmedPunches : [];
  const seen = new Set(
    confirmed.map((item) => `${item.punch}|${item.punchDateTime}`)
  );

  return [
    ...confirmed,
    ...(Array.isArray(queueItems) ? queueItems : []).filter((item) => {
      if (!hasValue(item?.punch) || !hasValue(item?.punchDateTime)) {
        return false;
      }

      const key = `${item.punch}|${item.punchDateTime}`;

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    }),
  ];
};

export const pickNewerClockConnectPayload = (
  existingPayload,
  incomingPayload,
  { existingCachedAt, incomingCachedAt } = {}
) => {
  if (!existingPayload) {
    return incomingPayload ?? null;
  }

  if (!incomingPayload) {
    return existingPayload;
  }

  const existingLastPunchMs = getDateTimeMs(existingPayload.lastPunchUTC);
  const incomingLastPunchMs = getDateTimeMs(incomingPayload.lastPunchUTC);

  if (hasValue(existingLastPunchMs) || hasValue(incomingLastPunchMs)) {
    if (!hasValue(incomingLastPunchMs)) {
      return existingPayload;
    }

    if (!hasValue(existingLastPunchMs)) {
      return incomingPayload;
    }

    return incomingLastPunchMs >= existingLastPunchMs
      ? incomingPayload
      : existingPayload;
  }

  const existingScore = getCacheTimeMs(existingCachedAt);
  const incomingScore = getCacheTimeMs(incomingCachedAt ?? new Date());

  return incomingScore >= existingScore ? incomingPayload : existingPayload;
};

export const readOfflineClockConnectCacheSnapshot = (userKey) => {
  const snapshot = parseCacheSnapshot(userKey);
  if (!snapshot) {
    return null;
  }

  const payload = extractPayload(snapshot);
  if (!payload) {
    return null;
  }

  const offlinePunch = pickAllowedOfflinePunchFields(snapshot.offlinePunch);
  const confirmedPunches = sanitizeConfirmedPunches(snapshot.confirmedPunches);

  return {
    cachedAt: snapshot.cachedAt,
    source: snapshot.source || CLOCK_CONNECT_CACHE_SOURCE,
    payload,
    ...(offlinePunch && { offlinePunch }),
    ...(confirmedPunches.length > 0 && { confirmedPunches }),
  };
};

const readStoredOfflinePunch = (userKey) =>
  pickAllowedOfflinePunchFields(parseCacheSnapshot(userKey)?.offlinePunch);

const readStoredConfirmedPunches = (userKey) =>
  sanitizeConfirmedPunches(parseCacheSnapshot(userKey)?.confirmedPunches);

const isOfflinePunchNewer = (payload, offlinePunch) => {
  const offlinePunchMs = getDateTimeMs(offlinePunch?.punchDateTime);
  if (!hasValue(offlinePunchMs)) {
    return false;
  }

  const apiLastPunchMs = getDateTimeMs(payload?.lastPunchUTC);
  return !hasValue(apiLastPunchMs) || offlinePunchMs > apiLastPunchMs;
};

/**
 * Display view: the API payload, with the offline punch laid over it only
 * when that punch is newer than the API's last punch.
 */
export const buildClockConnectDisplayPayload = (payload, offlinePunch) => {
  if (!payload) {
    return null;
  }

  if (!isOfflinePunchNewer(payload, offlinePunch)) {
    return payload;
  }

  return {
    ...payload,
    lastPunch: offlinePunch.lastPunch,
    lastPunchUTC: offlinePunch.punchDateTime,
    disabledButtonMap: offlinePunch.disabledButtonMap,
    enableButtons: offlinePunch.enableButtons,
    disableButtons: offlinePunch.disableButtons,
  };
};

export const readOfflineClockConnectCache = (userKey) => {
  const snapshot = readOfflineClockConnectCacheSnapshot(userKey);
  if (!snapshot) {
    return null;
  }

  return buildClockConnectDisplayPayload(
    snapshot.payload,
    snapshot.offlinePunch
  );
};

/**
 * Keep cache writes ordered. A sync write runs immediately; a write that
 * returns a promise holds the tail so the next read sees that result.
 */
const trackCacheOperation = (result) => {
  if (!isFunction(result?.then)) {
    return result;
  }

  let tracked;
  tracked = result.finally(() => {
    if (cacheOpTail === tracked) {
      cacheOpTail = null;
    }
  });
  cacheOpTail = tracked;
  return result;
};

const runCacheOperation = (operation) => {
  if (!cacheOpTail) {
    try {
      return trackCacheOperation(operation());
    } catch (error) {
      console.warn('[offlineClockConnectCache] Cache operation failed.', error);
      return null;
    }
  }

  const previous = cacheOpTail;
  const result = previous.then(
    () => operation(),
    () => operation()
  );
  return trackCacheOperation(result);
};

const writeOfflineClockConnectCacheNow = (userKey, clockConnectData) => {
  if (isEmpty(clockConnectData)) {
    return false;
  }

  const sanitizedPayload = normalizeClockConnectPayload(clockConnectData);
  if (!sanitizedPayload) {
    return false;
  }

  return persistSanitizedCachePayload(
    userKey,
    sanitizedPayload,
    readStoredOfflinePunch(userKey),
    readStoredConfirmedPunches(userKey)
  );
};

export const writeOfflineClockConnectCache = (userKey, clockConnectData) =>
  runCacheOperation(() =>
    writeOfflineClockConnectCacheNow(userKey, clockConnectData)
  );

/**
 * Persist an already-sanitised payload without re-running pickAllowedCacheFields.
 */
const persistSanitizedCachePayload = (
  userKey,
  sanitizedPayload,
  sanitizedOfflinePunch = null,
  confirmedPunches = null
) => {
  const storageKey = getStorageKey(userKey);
  if (!storageKey || !isStorageAvailable() || !sanitizedPayload) {
    return false;
  }

  const snapshot = buildCacheSnapshotFromSanitized(
    sanitizedPayload,
    sanitizedOfflinePunch,
    confirmedPunches
  );
  if (!snapshot || !isValidCacheSnapshot(snapshot)) {
    return false;
  }

  try {
    const serializedSnapshot = JSON.stringify(snapshot);
    const saved = writeOfflineWebClockItem(storageKey, serializedSnapshot);

    if (isFunction(saved?.then)) {
      return saved
        .then((written) => {
          if (written) {
            notifyCacheRevisionChanged();
          }

          return written;
        })
        .catch((error) => {
          console.warn(
            '[offlineClockConnectCache] Failed to write cache.',
            error
          );
          return false;
        });
    }

    if (saved) {
      notifyCacheRevisionChanged();
    }

    return saved;
  } catch (error) {
    console.warn('[offlineClockConnectCache] Failed to write cache.', error);
    return false;
  }
};

const mergeOfflineClockConnectCacheNow = async (userKey, incomingData) => {
  const incomingPayload = normalizeClockConnectPayload(incomingData);
  if (!incomingPayload) {
    return null;
  }

  const existingSnapshot = readOfflineClockConnectCacheSnapshot(userKey);
  const queueItems = readOfflinePunchQueue(userKey);
  const hasLocalOverlay =
    hasValue(existingSnapshot?.offlinePunch) ||
    !isEmpty(existingSnapshot?.confirmedPunches) ||
    !isEmpty(queueItems);
  const payload = hasLocalOverlay
    ? pickNewerClockConnectPayload(existingSnapshot?.payload, incomingPayload, {
        existingCachedAt: existingSnapshot?.cachedAt,
      })
    : incomingPayload;

  if (!payload) {
    return null;
  }

  try {
    const displayPayload = await persistPunchPrediction(
      userKey,
      payload,
      existingSnapshot?.confirmedPunches,
      queueItems,
      existingSnapshot?.offlinePunch,
      { retainExistingOverlay: true }
    );

    return displayPayload ? payload : null;
  } catch (error) {
    console.warn('[offlineClockConnectCache] Failed to merge cache.', error);
    return null;
  }
};

export const mergeOfflineClockConnectCache = (userKey, incomingData) =>
  runCacheOperation(() =>
    mergeOfflineClockConnectCacheNow(userKey, incomingData)
  );

/**
 * Pure: apply one punch to a button state and return the next state.
 */
const applyOfflinePunchTransition = (buttonState, { punch, punchDateTime }) => {
  const nextDisabledButtonMap = { ...buttonState.disabledButtonMap };
  const transition = PUNCH_BUTTON_TRANSITIONS[punch];
  const clickedButtonValue = PUNCH_TO_BUTTON_VALUE[punch];
  const pairedButtonValue = clickedButtonValue
    ? TOGGLE_PAIRS[clickedButtonValue]
    : null;

  if (transition?.exclusiveEnable) {
    ALL_PUNCH_BUTTON_VALUES.forEach((buttonValue) => {
      nextDisabledButtonMap[buttonValue] =
        buttonValue !== transition.exclusiveEnable;
    });
  } else if (transition) {
    transition.disable.forEach((buttonValue) => {
      nextDisabledButtonMap[buttonValue] = true;
    });
    transition.enable.forEach((buttonValue) => {
      nextDisabledButtonMap[buttonValue] = false;
    });
  } else {
    if (clickedButtonValue) {
      nextDisabledButtonMap[clickedButtonValue] = true;
    }
    if (pairedButtonValue) {
      nextDisabledButtonMap[pairedButtonValue] = false;
    }
  }

  const buttonValues = new Set([
    ...(Array.isArray(buttonState.enableButtons)
      ? buttonState.enableButtons
      : []),
    ...(Array.isArray(buttonState.disableButtons)
      ? buttonState.disableButtons
      : []),
    ...Object.keys(nextDisabledButtonMap),
  ]);

  return {
    punch,
    punchDateTime,
    lastPunch: getLastPunchDisplayLabel(punch, buttonState.lastPunch),
    disabledButtonMap: nextDisabledButtonMap,
    enableButtons: Array.from(buttonValues).filter(
      (buttonValue) => !nextDisabledButtonMap[buttonValue]
    ),
    disableButtons: Array.from(buttonValues).filter(
      (buttonValue) => nextDisabledButtonMap[buttonValue]
    ),
  };
};

/**
 * Pure: queued punches newer than the API's last punch, applied in
 * punchDateTime order on top of the API button state. Null when none apply.
 */
export const buildOfflinePunchPrediction = (payload, queueItems) => {
  if (!payload || !Array.isArray(queueItems)) {
    return null;
  }

  const apiLastPunchMs = getDateTimeMs(payload.lastPunchUTC);
  const newerItems = queueItems
    .map((item) => ({ item, punchMs: getDateTimeMs(item?.punchDateTime) }))
    .filter(
      ({ item, punchMs }) =>
        hasValue(item?.punch) &&
        hasValue(punchMs) &&
        (!hasValue(apiLastPunchMs) || punchMs > apiLastPunchMs)
    )
    .sort((left, right) => left.punchMs - right.punchMs)
    .map(({ item }) => item);

  if (isEmpty(newerItems)) {
    return null;
  }

  return newerItems.reduce(applyOfflinePunchTransition, {
    lastPunch: payload.lastPunch,
    disabledButtonMap: payload.disabledButtonMap,
    enableButtons: payload.enableButtons,
    disableButtons: payload.disableButtons,
  });
};

const persistPunchPrediction = (
  userKey,
  payload,
  confirmedPunches,
  queueItems,
  existingOfflinePunch,
  { retainExistingOverlay = false } = {}
) => {
  const trimmedConfirmedPunches = trimConfirmedPunches(
    confirmedPunches,
    payload
  );
  const predictedPunch = buildOfflinePunchPrediction(
    payload,
    predictionSources(trimmedConfirmedPunches, queueItems)
  );
  const retainedOfflinePunch =
    retainExistingOverlay &&
    !predictedPunch &&
    isOfflinePunchNewer(payload, existingOfflinePunch)
      ? existingOfflinePunch
      : null;
  const nextOfflinePunch = pickAllowedOfflinePunchFields(
    predictedPunch || retainedOfflinePunch
  );
  const displayPayload = buildClockConnectDisplayPayload(
    payload,
    nextOfflinePunch
  );
  const saved = persistSanitizedCachePayload(
    userKey,
    payload,
    nextOfflinePunch,
    trimmedConfirmedPunches
  );

  if (isFunction(saved?.then)) {
    return saved
      .then((written) => (written ? displayPayload : false))
      .catch(() => false);
  }

  return saved ? displayPayload : false;
};

const rebuildOfflinePunchPredictionNow = (userKey, queueItems) => {
  const existingSnapshot = readOfflineClockConnectCacheSnapshot(userKey);
  if (!existingSnapshot?.payload) {
    return null;
  }

  return persistPunchPrediction(
    userKey,
    existingSnapshot.payload,
    existingSnapshot.confirmedPunches,
    queueItems,
    existingSnapshot.offlinePunch,
    { retainExistingOverlay: false }
  );
};

const recordCompletedMobilePunchNow = (
  userKey,
  { punch, punchDateTime } = {}
) => {
  if (!hasValue(punch) || !hasValue(getDateTimeMs(punchDateTime))) {
    return null;
  }

  const existingSnapshot = readOfflineClockConnectCacheSnapshot(userKey);
  if (!existingSnapshot?.payload) {
    return null;
  }

  const existingConfirmedPunches = existingSnapshot.confirmedPunches || [];
  const alreadyConfirmed = existingConfirmedPunches.some(
    (item) => item.punch === punch && item.punchDateTime === punchDateTime
  );
  const nextConfirmedPunches = trimConfirmedPunches(
    alreadyConfirmed
      ? existingConfirmedPunches
      : [...existingConfirmedPunches, { punch, punchDateTime }],
    existingSnapshot.payload
  );

  if (
    !nextConfirmedPunches.some(
      (item) => item.punch === punch && item.punchDateTime === punchDateTime
    )
  ) {
    return buildClockConnectDisplayPayload(
      existingSnapshot.payload,
      existingSnapshot.offlinePunch
    );
  }

  return persistPunchPrediction(
    userKey,
    existingSnapshot.payload,
    nextConfirmedPunches,
    readOfflinePunchQueue(userKey),
    existingSnapshot.offlinePunch
  );
};

/**
 * Replace the stored offline punch prediction with one built from
 * queueItems. The API payload is never modified.
 * Returns the display payload, false when the write fails, or null when
 * there is no cached Clock Connect payload.
 */
export const rebuildOfflinePunchPrediction = (userKey, queueItems) =>
  runCacheOperation(() =>
    rebuildOfflinePunchPredictionNow(userKey, queueItems)
  );

/**
 * Remember a mobile punch that MobilePunch or MobileOfflinePunch has already
 * accepted. The derived button state stays ahead of a stale MobileClockConnect
 * response until that response's lastPunchUTC catches up.
 */
export const recordCompletedMobilePunch = (userKey, completedPunch) =>
  runCacheOperation(() =>
    recordCompletedMobilePunchNow(userKey, completedPunch)
  );
